import { getBufferLayout, getBufferLayoutHash } from '../Pipelines.js';
import { UniformGroup } from '../utils/UniformGroup.ts';
import { MipmapPipeline } from './MipmapPipeline.js';
import { initTexture, createSamplerDesc, createSamplerKey } from '../Texture.js';
import { $wgsl } from "../../wgsl-preprocessor/wgsl-preprocessor.js";
import { FrameBindGroup } from "./FrameBindGroup.js";
import { getObjectUniformsDeclaration } from "./uniforms/ObjectUniforms.js";
import { ShaderMaterialWebGPU } from "./ShaderMaterialWebGPU.js";
import { sideToCullMode } from "../compat.js";
import { getDepthBiasValues } from "./DepthBiasMap";
import { DepthFormat, invertDepthCompare } from "../CommonRenderTargets.js";
import { BufferGeometryUtils } from "../../scene/BufferGeometry.js";
import { ToneMapMethod } from "../Renderer";

// Shader chunks
import pack_depth from "../chunks/pack_depth.wgsl";
import { getVertexTransformChunk } from "../chunks/vertex_transform.js";
import pack_normals from "../chunks/pack_normals.wgsl";
import theming_and_highlighting from "../chunks/theming_and_highlighting.wgsl";
import mat_inverse from "../chunks/mat-inverse.wgsl";
import { getEnvMapSampleChunk } from "../chunks/env_sample";
import tonemap from "../chunks/tonemap.wgsl";
import heatmap from "../chunks/heatmap.wgsl";
import normal_map from "../chunks/normal_map.wgsl";

/** @import { VertexBuffer } from '../VertexBuffer.js' */

/*
How to extend a Three.ShaderMaterial for WebGPU

MainPass will catch the material and use it with CustomShaderPipeline.
The pipeline will have frameBindGroup and objectUniforms always.
Custom materials will use bind group 2.
All uniforms of simple types are stored in a single uniform buffer with binding 0.
The uniform buffer in the shader must list the uniforms in the same order as in material.uniforms.
Entrypoints for shaders are fixed as vsmain and psmain.

Before material is added to the scene, we need to add some information to it.
Here is how to do it, with types:

Use samplerBinding in textureBindings if you want to create a sampler from a THREE.texture uniform.
Use samplers if you want to define your own sampler or when binding a GPUTexture.
Defaults are inserted in UniformGroup.ts.

material.webGPU = {
    labelPrefix: string,
    shader?: string,
    uniformBufferVisibility?: GPUShaderStageFlags
    uniformBufferBindingLayout?: GPUBufferBindingLayout
    textureBindings?: [
        {
            uniformKey: string,
            binding: number,
            textureVisibility?: GPUShaderStageFlags,
            sampleType?: GPUTextureSampleType,
            samplerBinding?: number,
            sampleVisibility?: GPUShaderStageFlags
        }
    ],
    samplers?: [
        {
            binding: number,
            visibility?: GPUShaderStageFlags,
            samplerDesc?: GPUSamplerDescriptor

        }
    ]
}
*/


const CustomMaterialBindGroupIndex = 2;

function getCustomShader(shaderSource, defines, categoryHighlightingEnabled = false, categoryHighlightingFallback = false, isGamma, isRGBM, gammaInput, useIrradianceMap, useUnorm16 = false, hasNormals = true) {
  // hasCategoryTarget: true when category highlighting uses a dedicated render target (not fallback mode)
  const hasCategoryTarget = categoryHighlightingEnabled && !categoryHighlightingFallback;
  // TODO: Might add hasUV, isUVW for shaders to use as well?
  return $wgsl(shaderSource, {
    // general chunks
    frameBindGroup: FrameBindGroup.getDeclaration(0),
    objectUniforms: getObjectUniformsDeclaration(1, false),
    mat_inverse,
    // Vertex shader chunks
    vertexTransform: getVertexTransformChunk(),
    pack_normals,
    useUnorm16,
    hasNormals,
    // Fragment shader chunks
    pack_depth,
    theming_and_highlighting,
    categoryHighlighting: categoryHighlightingEnabled,
    categoryHighlightingFallback,
    hasCategoryTarget,
    normal_map,
    tonemap,
    heatmap,

    useIrradianceMap,
    envMapSample: getEnvMapSampleChunk(isGamma, isRGBM, gammaInput),
    gammaInput,
    // Custom
    ...defines
  });
}

/**
 * Pipeline hash for custom shader materials.
 * Uses geometry layout hash in lower bits and material.id in upper bits.
 * @param {THREE.BufferGeometry} geometry
 * @param {THREE.Material} material
 * @returns {BigInt}
 */
function getCustomPipelineHash(geometry, material) {
  const attributes = geometry.attributes;
  const includeUV = !!attributes.uv;
  const includeVC = !!attributes.color;
  const geomLayoutHash = getBufferLayoutHash(geometry, includeUV, includeVC, false);
  return BigInt(geomLayoutHash) | BigInt(material.id) << 19n;
}

export class CustomShaderPipeline {
  #renderer;
  #device;

  #pipelines = new Map();
  #mipmapPipeline;

  #activeBindGroupLayout;
  #activeTargetsList;
  /** @type {VertexBuffer} */
  #vb;

  #boundOnMaterialUpdate = this.#onMaterialUpdate.bind(this);
  #boundOnMaterialDispose = this.#onMaterialDispose.bind(this);

  constructor(renderer) {
    this.#renderer = renderer;
    this.#device = renderer.getDevice();
    this.#mipmapPipeline = new MipmapPipeline(this.#device);
    this.#vb = this.#renderer.getVB();
  }

  #createPipeline(key, geometry, material) {
    // TODO:
    //  - Currently, we just add everything available to the vertex layout.
    //    So, we might bind more than needed.
    //  - VertexLayout is only considering basic types (like position/normal/uv), but should be more flexible.
    const attributes = geometry.attributes;
    const includeVC = attributes.color;
    const isUnorm16 = geometry.hasUnorm16Layout?.();
    // Whether getBufferLayout() will actually bind a location-1 (normal) vertex attribute below.
    // Custom shaders (e.g. prism.wgsl) must guard their own @location(1) normal input on this same
    // flag, or the VertexState and the shader module's input layout fall out of sync.
    const hasNormals = !geometry.isLines && Boolean(attributes.normal) && !isUnorm16;
    const hasUV = attributes.uv;
    const isUVW = Boolean(attributes.uvw); // Wood materials use 3D UVW coordinates

    let label;
    let pipeline;
    let vertexShader, vertexEntryPoint;
    let fragmentShader, fragmentEntryPoint;
    let materialUniforms;

    const categoryHighlightingEnabled = this.#renderer.isCategoryHighlightingEnabled();
    const categoryHighlightingFallback = this.#renderer.isCategoryHighlightingFallback();

    const useIrradianceMap = this.#renderer.getIBL().hasIrradianceMap();

    // Activate gamma-decoding for input colors if tone-mapping is used
    // (e.g., see inputToLinear() in uber.wgsl)
    const gammaInput = this.#renderer.getTonemapMethod() != ToneMapMethod.None;
    const { isGamma, isRGBM } = this.#renderer.getEnvMapEncoding();

    // Legacy THREE.ShaderMaterials are upgraded via the webGPU property.
    if (material instanceof THREE.ShaderMaterial) {
      label = material.webGPU.labelPrefix ?? `Material-${key}`;

      // Upgraded THREE.ShaderMaterials don't support custom defines yet.
      const defines = {};

      const shader = this.#device.createShaderModule({
        label: `${label} Shader`,
        code: getCustomShader(material.webGPU.shader, {}, categoryHighlightingEnabled, categoryHighlightingFallback, isGamma, isRGBM, gammaInput, useIrradianceMap, isUnorm16, hasNormals)
      });
      vertexShader = shader;
      vertexEntryPoint = 'vsmain';
      fragmentShader = shader;
      fragmentEntryPoint = 'psmain';

      materialUniforms = material.__gpuCustomMaterialUniforms;
    } else if (material instanceof ShaderMaterialWebGPU) {
      label = material.label ?? `Material-${key}`;

      const defines = material.defines || {};

      /** @todo Should this be optimized to create a single module when fragment.src == vertex.src?*/

      fragmentShader = this.#device.createShaderModule({
        label: `${label} Fragment Shader`,
        code: getCustomShader(material.fragmentShader.src, defines, categoryHighlightingEnabled, categoryHighlightingFallback, isGamma, isRGBM, gammaInput, useIrradianceMap, isUnorm16, hasNormals)
      });
      fragmentEntryPoint = material.fragmentShader.entryPoint; // might be undefined, which is fine

      vertexShader = this.#device.createShaderModule({
        label: `${label} Vertex Shader`,
        code: getCustomShader(material.vertexShader.src, defines, categoryHighlightingEnabled, categoryHighlightingFallback, isGamma, isRGBM, gammaInput, useIrradianceMap, isUnorm16, hasNormals)
      });
      vertexEntryPoint = material.vertexShader.entryPoint; // might be undefined, which is fine

      materialUniforms = material.uniformGroup;
    } else {
      console.error('Unsupported material type in CustomShaderPipeline:', material);
    }

    // Unlike UberShader, which uses a single bind group layout for all materials, the layout may
    // vary for this case. Therefore, we replace the material uniform bind group layout at slot 2 with the
    // individual bindGroupLayout of the current material.
    const bindGroupLayouts = this.#activeBindGroupLayout.slice();
    bindGroupLayouts[CustomMaterialBindGroupIndex] = materialUniforms.layout;

    const depthCompare = material.depthTest ? invertDepthCompare(material.depthFunc || 'less-equal') : 'always';
    const sampleCount = this.#renderer.getMSAASampleCount();

    pipeline = this.#device.createRenderPipeline({
      label: `${label} Pipeline`,
      layout: this.#device.createPipelineLayout({
        label: `${label} Pipeline Layout`,
        bindGroupLayouts
      }),
      vertex: {
        module: vertexShader,
        entryPoint: vertexEntryPoint,
        buffers: getBufferLayout(geometry, hasNormals, hasUV, includeVC, isUVW)
      },
      fragment: {
        module: fragmentShader,
        entryPoint: fragmentEntryPoint,
        targets: this.#activeTargetsList
      },
      primitive: {
        topology: 'triangle-list',
        cullMode: sideToCullMode(material.side)
      },

      depthStencil: {
        depthWriteEnabled: material.depthWrite,
        depthCompare,
        format: DepthFormat,
        ...getDepthBiasValues(material)
      },
      multisample: {
        count: sampleCount
      }
    });

    this.#pipelines.set(key, pipeline);

    return pipeline;
  }

  /** Clears all cached pipelines. Call when MSAA settings change. */
  resetPipelines() {
    this.#pipelines.clear();
  }

  reset(layouts, targets) {
    this.#activeBindGroupLayout = layouts;
    this.#activeTargetsList = targets;
  }

  /**
   * Initializes texture bindings for materials that use textureBindings configuration.
   * Adds all texture and sampler bindings to the UniformGroup using placeholder textures.
   * This ensures the bind group layout has all required bindings, even if textures aren't loaded yet.
   * @param {ShaderMaterialWebGPU} material - The material to initialize texture bindings for
   */
  #initTextureBindings(material) {
    const uniformGroup = material.uniformGroup;
    if (!uniformGroup) {
      return false;
    }

    const placeholderTexture = this.#renderer.getPlaceholderTexture().texture;

    for (const binding of material.textureBindings) {
      // Add texture binding with placeholder texture
      uniformGroup.addTexture(
        binding.binding,
        placeholderTexture,
        binding.textureVisibility || GPUShaderStage.FRAGMENT,
        binding.sampleType || 'float'
      );

      // Add sampler binding if specified
      if (binding.samplerBinding !== undefined) {
        uniformGroup.addSampler(
          binding.samplerBinding,
          binding.samplerVisibility || GPUShaderStage.FRAGMENT,
          {} // Use default sampler descriptor
        );
      }
    }
  }

  /**
   * Updates textures for materials that use textureBindings configuration.
   * Separates GPU upload (gated by texture.needsUpdate, once per texture) from
   * bind group binding (tracked per-material via __gpuBoundTextures, so shared
   * textures are correctly bound into each material's UniformGroup).
   * Sampler parameters are tracked separately in __gpuBoundSamplers so that
   * changes (e.g. wrap mode, filter) are detected even when gpuTexture identity is unchanged.
   * @param {ShaderMaterialWebGPU} material - The material to update textures for
   * @returns {boolean} True if any textures were updated (and bind group needs recreation)
   */
  #updateTextures(material) {
    const uniformGroup = material.uniformGroup;
    if (!uniformGroup) {
      return false;
    }

    let texturesUpdated = false;

    for (const binding of material.textureBindings) {
      const texture = material[binding.uniformKey];
      if (!texture) {
        // Texture removed from material — clear stale entry and flag for bind group recreation
        if (material.__gpuBoundTextures.delete(binding.binding)) {
          uniformGroup.setTexture(null, binding.binding);
          if (binding.samplerBinding !== undefined) {
            material.__gpuBoundSamplers.delete(binding.samplerBinding);
          }
          texturesUpdated = true;
        }
        continue;
      }

      // Upload to GPU if needed (runs once per texture, not per material)
      if (texture.needsUpdate) {
        initTexture(this.#device, this.#mipmapPipeline, texture);
      }

      // Bind to this material's uniform group if not already bound.
      // Separate from upload: a shared texture may already be on GPU
      // but not yet bound in THIS material's bind group.
      const gpuTexture = texture.__gpuTexture;
      if (!gpuTexture) {
        // Texture was disposed — clear UniformGroup binding so the bind group
        // doesn't hold a stale GPU reference, and reset tracking maps.
        uniformGroup.setTexture(null, binding.binding);
        material.__gpuBoundTextures.delete(binding.binding);
        if (binding.samplerBinding !== undefined) {
          material.__gpuBoundSamplers.delete(binding.samplerBinding);
        }
        texturesUpdated = true;
        continue;
      }

      if (material.__gpuBoundTextures.get(binding.binding) !== gpuTexture) {
        uniformGroup.setTexture(gpuTexture, binding.binding);
        material.__gpuBoundTextures.set(binding.binding, gpuTexture);
        texturesUpdated = true;
      }

      if (binding.samplerBinding !== undefined) {
        const samplerDesc = createSamplerDesc(texture);
        const samplerKey = createSamplerKey(samplerDesc);
        if (material.__gpuBoundSamplers.get(binding.samplerBinding) !== samplerKey) {
          uniformGroup.setSampler(samplerDesc, binding.samplerBinding);
          material.__gpuBoundSamplers.set(binding.samplerBinding, samplerKey);
          texturesUpdated = true;
        }
      }
    }

    return texturesUpdated;
  }


  // Handler for the 'update' event dispatched when mat.needsUpdate = true is set.
  // Needs to invalidate render bundles if binding resources changed.
  #onMaterialUpdate() {
    this.#renderer.invalidateRenderBundles();
  }

  #onMaterialDispose(event) {
    const material = event.target;
    // Optional chaining: these maps only exist on ShaderMaterialWebGPU, not on legacy THREE.ShaderMaterial.
    material.__gpuBoundTextures?.clear();
    material.__gpuBoundSamplers?.clear();
    material.removeEventListener('dispose', this.#boundOnMaterialDispose);
    material.removeEventListener('update', this.#boundOnMaterialUpdate);
  }

  /**
   * @param {THREE.ShaderMaterial | ShaderMaterialWebGPU} material
   * @return {GPUBindGroup} The bind group to use
   */
  #initCustomMaterialBindGroup(material) {

    // Analogous to initMaterialBindings for UberPipeline we install an update listener to make sure
    // RenderBundles are invalidated whenever bindGroup resources are affected by a change.
    if (!material.hasEventListener('update', this.#boundOnMaterialUpdate)) {
      material.addEventListener('update', this.#boundOnMaterialUpdate);
      material.addEventListener('dispose', this.#boundOnMaterialDispose);
    }

    let CustomMaterialBindGroup = null;

    if (material instanceof THREE.ShaderMaterial) {
      if (!material.__gpuCustomMaterialUniforms) {
        // Create uniform group the first time around.
        const customMaterialUniforms = UniformGroup.createFromCustomMaterial(this.#device, this.#renderer, material, this.#mipmapPipeline);
        material.__gpuCustomMaterialUniforms = customMaterialUniforms;

        material.__gpuCustomMaterialUniforms.update(this.#device);
      } else if (material.uniformsNeedUpdate) {
        material.__gpuCustomMaterialUniforms.updateFromMaterial(this.#device, material);
        material.uniformsNeedUpdate = false;
      }

      CustomMaterialBindGroup = material.__gpuCustomMaterialUniforms.bindGroup;
    } else if (material instanceof ShaderMaterialWebGPU) {

      // One-time initialization.
      if (!material.uniformGroup.isDefaultTextureSet()) {
        const placeholderTexture = this.#renderer.getPlaceholderTexture().texture;
        material.uniformGroup.setDefaultTexture(placeholderTexture);

        // Initialize texture bindings with placeholder textures
        this.#initTextureBindings(material);
      }

      const uniformsUpdated = material.uniformsNeedUpdate || material.needsUpdate;

      if (uniformsUpdated) {
        // This allows materials to update their uniform values from properties.
        material.refreshUniforms();
      }

      const texturesUpdated = this.#updateTextures(material);

      // If uniforms or textures were updated, the bind group was invalidated - recreate it
      if (uniformsUpdated || texturesUpdated) {
        material.uniformGroup.update(this.#device);
        material.uniformsNeedUpdate = false;
      }

      CustomMaterialBindGroup = material.uniformGroup.bindGroup;
    }

    return CustomMaterialBindGroup;
  }

  drawOne(passEncoder, objectIndex, geometry, material) {
    // HACK: If geometry is only provided as already interleaved, there is nothing to update.
    // TODO: We need a cleaner concept to distinguish "augumented interleaved geom" and the case that source geometry
    //       is already interleaved and there is no non-interleaved geometry to update from.
    //       Note  that the same hack exists in Renderer.initGeometry() when changing this.
    const isNonInterleaved = geometry.attributes.position.array;
    if (!geometry.vb || geometry.vbNeedsUpdate && isNonInterleaved) {
      BufferGeometryUtils.interleaveGeometry(geometry, true);
    }

    // Set up custom material uniforms.
    // TODO: Handle modifications, disposal etc.
    const materialBindGroup = this.#initCustomMaterialBindGroup(material);
    passEncoder.setBindGroupIfNeeded(CustomMaterialBindGroupIndex, materialBindGroup);

    // ShaderMaterials require custom shaders, so we just brutally create/store a pipeline per material for now.
    // TODO: We should allow custom materials to give hints about which ones really need a pipeline change.
    const key = getCustomPipelineHash(geometry, material);
    let pipeline = this.#pipelines.get(key);
    if (!pipeline || material.needsUpdate) {
      pipeline = this.#createPipeline(key, geometry, material);
      material.needsUpdate = false;
    }

    passEncoder.setPipelineIfNeeded(pipeline);

    this.#vb.draw(passEncoder, geometry, objectIndex);
  }

}